import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { accountAPI, postAPI } from '../../services/api'

function UserDashboardPage({ onNavigateToAutomation }) {
  const { user } = useAuth()
  const [account, setAccount] = useState(null)
  const [stats, setStats] = useState({
    totalUploaded: 0,
    totalPending: 0,
    totalFailed: 0,
    totalPosts: 0,
  })
  const [recentNews, setRecentNews] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user?.username) {
      loadUserDashboard()
    }
  }, [user])

  const loadUserDashboard = async () => {
    try {
      setLoading(true)

      // 1. 로그인한 사용자의 계정 조회
      const accountRes = await accountAPI.getMy()
      if (!accountRes.success || !accountRes.data?.length) {
        setLoading(false)
        return
      }

      const myAccount = accountRes.data.find(a => a.status === 'ACTIVE' && a.platformUserId) || accountRes.data[0]
      setAccount(myAccount)

      // 2. 해당 계정의 전체 게시물 조회 (account_id 기반)
      const postsRes = await postAPI.getByAccount(myAccount.id)
      const allPosts = postsRes.success ? (postsRes.data || []) : []

      const uploaded = allPosts.filter(n => n.uploadStatus === 'UPLOADED')
      const pending = allPosts.filter(n => n.uploadStatus === 'PENDING')
      const failed = allPosts.filter(n => n.uploadStatus === 'FAILED')

      setStats({
        totalUploaded: uploaded.length,
        totalPending: pending.length,
        totalFailed: failed.length,
        totalPosts: allPosts.length,
      })

      setRecentNews(uploaded.slice(0, 5))
    } catch (err) {
      console.error('사용자 대시보드 로드 실패:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-slate-500 dark:text-slate-400 text-lg">로딩 중...</div>
      </div>
    )
  }

  if (!account) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-600">person_off</span>
        <p className="text-slate-500 dark:text-slate-400 text-lg">연결된 인스타그램 계정이 없습니다</p>
        <p className="text-slate-400 dark:text-slate-500 text-sm">자동화 설정에서 계정을 먼저 등록해주세요</p>
      </div>
    )
  }

  const needsSetup = account.status === 'PENDING_SETUP' || !account.hasAccessToken || !account.platformUserId

  return (
    <div>
      {/* Instagram 설정 미완료 알림 */}
      {needsSetup && (
        <div className="mb-6 p-4 sm:p-5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <span className="material-symbols-outlined text-amber-500 text-2xl shrink-0">warning</span>
            <div>
              <p className="font-bold text-amber-800 dark:text-amber-300 text-sm sm:text-base">Instagram 연동 설정을 완료해주세요</p>
              <p className="text-xs sm:text-sm text-amber-600 dark:text-amber-400 mt-0.5">Business Account ID와 API 토큰을 설정해야 자동화 기능을 사용할 수 있습니다.</p>
            </div>
          </div>
          {onNavigateToAutomation && (
            <button
              onClick={onNavigateToAutomation}
              className="w-full sm:w-auto px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold text-sm whitespace-nowrap transition-colors"
            >
              자동화 설정으로 이동
            </button>
          )}
        </div>
      )}

      {/* 헤더 */}
      <header className="mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
          대시보드 현황
        </h2>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm sm:text-base">
          인스타그램 자동화 서비스의 실시간 지표입니다.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 왼쪽: 지표 + 차트 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 핵심 지표 카드 */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {/* 총 업로드 */}
            <div className="bg-white dark:bg-slate-900/50 p-3 sm:p-6 rounded-xl border border-slate-200 dark:border-slate-800">
              <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">총 업로드</p>
              <div className="flex items-end justify-between mt-1 sm:mt-2">
                <p className="text-lg sm:text-2xl font-bold dark:text-white">{stats.totalUploaded.toLocaleString()}</p>
                <span className="text-emerald-500 text-[10px] sm:text-sm font-semibold flex items-center">
                  <span className="material-symbols-outlined text-sm hidden sm:inline">check_circle</span> 완료
                </span>
              </div>
            </div>

            {/* 대기중 */}
            <div className="bg-white dark:bg-slate-900/50 p-3 sm:p-6 rounded-xl border border-slate-200 dark:border-slate-800">
              <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">대기중 큐</p>
              <div className="flex items-end justify-between mt-1 sm:mt-2">
                <p className="text-lg sm:text-2xl font-bold dark:text-white">{stats.totalPending.toLocaleString()}</p>
                <span className="text-amber-500 text-[10px] sm:text-sm font-semibold flex items-center">
                  <span className="material-symbols-outlined text-sm hidden sm:inline">schedule</span> 대기
                </span>
              </div>
            </div>

            {/* 실패 */}
            <div className="bg-white dark:bg-slate-900/50 p-3 sm:p-6 rounded-xl border border-slate-200 dark:border-slate-800">
              <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">실패</p>
              <div className="flex items-end justify-between mt-1 sm:mt-2">
                <p className="text-lg sm:text-2xl font-bold dark:text-white">{stats.totalFailed}</p>
                <span className={`text-[10px] sm:text-sm font-semibold ${stats.totalFailed > 0 ? 'text-red-500' : 'text-slate-400'}`}>
                  {stats.totalFailed > 0 ? '재시도 필요' : '없음'}
                </span>
              </div>
            </div>
          </div>

          {/* 업로드 상태 분포 */}
          <div className="bg-white dark:bg-slate-900/50 p-4 sm:p-6 rounded-xl border border-slate-200 dark:border-slate-800">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-2">
              <div>
                <h3 className="text-base sm:text-lg font-bold dark:text-white">업로드 상태 분포</h3>
                <p className="text-xs sm:text-sm text-slate-500">현재 상태 기준</p>
              </div>
              <div className="flex gap-3 sm:gap-4">
                <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-slate-400">
                  <span className="size-2 rounded-full bg-primary"></span> 완료
                </div>
                <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-slate-400">
                  <span className="size-2 rounded-full bg-amber-400"></span> 대기
                </div>
                <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-slate-400">
                  <span className="size-2 rounded-full bg-red-400"></span> 실패
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 items-end gap-4 sm:gap-12 h-36 sm:h-48 px-2 sm:px-4">
              {/* 완료 */}
              <div className="flex flex-col items-center gap-2 h-full justify-end">
                <div className="flex gap-1 w-full items-end justify-center">
                  <div
                    className="w-12 bg-primary rounded-t-sm transition-all"
                    style={{ height: `${Math.min(100, Math.max(10, (stats.totalUploaded / Math.max(stats.totalUploaded + stats.totalPending + stats.totalFailed, 1)) * 100))}%` }}
                  ></div>
                </div>
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                  완료 ({stats.totalUploaded})
                </span>
              </div>
              {/* 대기 */}
              <div className="flex flex-col items-center gap-2 h-full justify-end">
                <div className="flex gap-1 w-full items-end justify-center">
                  <div
                    className="w-12 bg-amber-400 rounded-t-sm transition-all"
                    style={{ height: `${Math.min(100, Math.max(10, (stats.totalPending / Math.max(stats.totalUploaded + stats.totalPending + stats.totalFailed, 1)) * 100))}%` }}
                  ></div>
                </div>
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                  대기 ({stats.totalPending})
                </span>
              </div>
              {/* 실패 */}
              <div className="flex flex-col items-center gap-2 h-full justify-end">
                <div className="flex gap-1 w-full items-end justify-center">
                  <div
                    className="w-12 bg-red-400 rounded-t-sm transition-all"
                    style={{ height: `${Math.min(100, Math.max(10, (stats.totalFailed / Math.max(stats.totalUploaded + stats.totalPending + stats.totalFailed, 1)) * 100))}%` }}
                  ></div>
                </div>
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                  실패 ({stats.totalFailed})
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 오른쪽: 자동화 상태 + 내 계정 정보 */}
        <div className="space-y-6">
          {/* 자동화 상태 */}
          <div className="bg-white dark:bg-slate-900/50 p-6 rounded-xl border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold dark:text-white">자동화 상태</h3>
              <div className={`flex h-6 w-10 items-center rounded-full p-0.5 ${account.autoUploadEnabled ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-700'}`}>
                <div className={`h-5 w-5 rounded-full bg-white transition-all ${account.autoUploadEnabled ? 'ml-auto' : ''}`}></div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className={`material-symbols-outlined ${account.autoUploadEnabled ? 'text-emerald-500' : 'text-slate-400'}`}>
                  {account.autoUploadEnabled ? 'check_circle' : 'pause_circle'}
                </span>
                <div>
                  <p className="text-sm font-semibold dark:text-white">
                    {account.autoUploadEnabled ? '정상 작동 중' : '중지됨'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {account.autoUploadEnabled ? '모든 프로세스가 활성화됨' : '자동 업로드가 비활성화 상태'}
                  </p>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-100 dark:border-slate-800 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">오늘 예정 게시물</span>
                  <span className="font-medium dark:text-slate-300">{stats.totalPending}개</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">오늘 게시 완료</span>
                  <span className="font-medium dark:text-slate-300">{account.todayPostCount || 0}건</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">전체 게시물</span>
                  <span className="font-medium dark:text-slate-300">{stats.totalPosts}개</span>
                </div>
              </div>
            </div>
          </div>

          {/* 내 계정 정보 */}
          <div className="bg-white dark:bg-slate-900/50 p-6 rounded-xl border border-slate-200 dark:border-slate-800">
            <h3 className="font-bold dark:text-white mb-4">내 계정 정보</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="size-12 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-lg font-bold">
                  {account.username?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-bold dark:text-white">@{account.username}</p>
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                    account.status === 'ACTIVE'
                      ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                      : account.status === 'PENDING_SETUP'
                      ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400'
                      : 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400'
                  }`}>
                    {account.status === 'ACTIVE' ? '활성' : account.status === 'PENDING_SETUP' ? '설정 대기' : account.status === 'INACTIVE' ? '비활성' : '토큰 오류'}
                  </span>
                </div>
              </div>
              <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">일일 게시 한도</span>
                  <span className="font-medium dark:text-slate-300">{account.todayPostCount || 0} / {account.dailyPostLimit || 25}건</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">업로드 시간</span>
                  <span className="font-medium dark:text-slate-300">
                    {account.uploadSchedule || '미설정'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">토큰 유형</span>
                  <span className="font-medium dark:text-slate-300">
                    {account.tokenType === 'PERMANENT' ? '영구 토큰' : '갱신형 토큰'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 최근 업로드 */}
      <section className="mt-6 sm:mt-8">
        <div className="bg-white dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
            <h3 className="font-bold text-base sm:text-lg dark:text-white">최근 업로드</h3>
            <span className="text-primary text-xs sm:text-sm font-medium">총 {stats.totalUploaded}건</span>
          </div>

          {/* 모바일: 카드 리스트 */}
          <div className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
            {recentNews.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                아직 업로드된 콘텐츠가 없습니다
              </div>
            ) : (
              recentNews.map(news => (
                <div key={news.id} className="p-4 flex items-center gap-3">
                  {news.thumbnailPath ? (
                    <img src={news.thumbnailPath} alt="" className="size-12 rounded-lg object-cover bg-slate-200 dark:bg-slate-700 shrink-0" onError={(e) => { e.target.style.display = 'none' }} />
                  ) : (
                    <div className="size-12 rounded-lg bg-slate-200 dark:bg-slate-700 shrink-0"></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold dark:text-white truncate">{news.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-500">{news.sourceType === 'GENERATED' ? 'AI 생성' : news.sourceType === 'MANUAL' ? '직접 등록' : news.sourceType === 'CALENDAR' ? '캘린더' : news.sourceType || '-'}</span>
                      <span className="text-xs text-slate-400">{news.uploadedAt ? new Date(news.uploadedAt).toLocaleDateString('ko-KR') : '-'}</span>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold rounded shrink-0">완료</span>
                </div>
              ))
            )}
          </div>

          {/* 데스크탑: 테이블 */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-slate-800/50">
                <tr>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">콘텐츠</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">유형</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">업로드 날짜</th>
                  <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase text-center">상태</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {recentNews.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-sm text-slate-500">
                      아직 업로드된 콘텐츠가 없습니다
                    </td>
                  </tr>
                ) : (
                  recentNews.map(news => (
                    <tr key={news.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {news.thumbnailPath ? (
                            <img src={news.thumbnailPath} alt="" className="size-10 rounded-lg object-cover bg-slate-200 dark:bg-slate-700" onError={(e) => { e.target.style.display = 'none' }} />
                          ) : (
                            <div className="size-10 rounded-lg bg-slate-200 dark:bg-slate-700"></div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-bold dark:text-white truncate max-w-[200px]">{news.title}</p>
                            <p className="text-xs text-slate-500 truncate max-w-[200px]">{news.content}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm dark:text-slate-300">{news.sourceType === 'GENERATED' ? 'AI 생성' : news.sourceType === 'MANUAL' ? '직접 등록' : news.sourceType === 'CALENDAR' ? '캘린더' : news.sourceType || '-'}</td>
                      <td className="px-6 py-4 text-sm dark:text-slate-300">{news.uploadedAt ? new Date(news.uploadedAt).toLocaleDateString('ko-KR') : '-'}</td>
                      <td className="px-6 py-4 text-center">
                        <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold rounded">완료</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  )
}

export default UserDashboardPage
